import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  RelationTable,
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { exemplairesColumnsVisibility } from "@/config/dashboard/exemplaires";
import { Multiplesample } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Badge } from "../ui/badge";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { MultiplesampleLinkGridCard } from "./relatedLinkGridCards";

interface MultiplesampleLinksProps {
  id: string;
  type: LinkableType;
  relationTable?: RelationTable;
}

const MultiplesampleLinks = ({
  id,
  type,
  relationTable = "relatedMultiplesample",
}: MultiplesampleLinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelated, setPaginationRelated] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    relationTable,
    getPaginationRelated.pageSize,
    getPaginationRelated.page * getPaginationRelated.pageSize,
  );

  const { data: countRelated } = useCountRelatedFields(id, type, [
    relationTable,
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    relationTable,
  );
  const { mutate: deleteRelation } = useDeleteRelation(id, type, relationTable);

  const [searchContent, setSearchContent] = useState("");
  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedMultiplesampleId, setSelectedMultiplesampleId] = useState<
    string | null
  >(null);
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedMultiplesamples } =
    useGetNotRelatedResource<Multiplesample>({
      resultType: "multiplesample",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
      anchorResourceType: type,
    });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (multiplesampleId: string) => {
      deleteRelation(multiplesampleId);
    },
    [deleteRelation],
  );

  const renderMultiplesampleRowActions = useCallback(
    (item: Multiplesample) => (
      <RelatedRecordRowActions
        isAddMode={isAddMode}
        resourceId={item.id}
        previewType="multiplesample"
        previewElements={[
          {
            label: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
            value: item.exemplaireDansEstampesMultiplesDescriptionNumero,
            type: "text",
          },
          {
            label: t("exemplaireDansEstampesMultiplesDescriptionNumeroInventaire"),
            value: item.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire,
            type: "text",
          },
          {
            label: t("exemplaireDansEstampesMultiplesDescriptionLocalisation"),
            value: item.exemplaireDansEstampesMultiplesDescriptionLocalisation,
            type: "text",
          },
          {
            label: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
            value: item.exemplaireDansEstampesMultiplesDescriptionNotes,
            type: "richtext",
          },
          {
            label: t("attachments"),
            value: item.attachments ?? [],
            type: "image",
          },
        ]}
        onDelete={() => handleDeleteRelation(item.id)}
        onAdd={() => handleCreateRelation(item.id)}
      />
    ),
    [handleCreateRelation, handleDeleteRelation, isAddMode, t],
  );

  const columns: ColumnDef<Multiplesample, unknown>[] = useMemo(() => {
    const isAuthorView = type === "author";
    return [
      {
        accessorKey: "exemplaireDansEstampesMultiplesDescriptionNumero",
        header: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
        cell: ({ row }) => (
          <div className="flex items-center gap-2 text-nowrap">
            <p className="text-[14px] font-[500]">
              {row.original.exemplaireDansEstampesMultiplesDescriptionNumero}
            </p>
          </div>
        ),
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="multiplesample"
          />
        ),
      },
      {
        accessorKey:
          "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
        header: t("exemplaireDansEstampesMultiplesDescriptionNumeroInventaire"),
        cell: ({ row }) => (
          <TextCell
            value={
              row.original
                .exemplaireDansEstampesMultiplesDescriptionNumeroInventaire
            }
          />
        ),
      },
      ...(!isAuthorView
        ? [
            {
              accessorKey:
                "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
              header: t(
                "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
              ),
              cell: ({ row }: { row: any }) => (
                <TextCell
                  value={
                    row.original
                      .exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement
                  }
                />
              ),
            },
            {
              accessorKey: "exemplaireDansEstampesMultiplesDescriptionStatus",
              header: t("exemplaireDansEstampesMultiplesDescriptionStatus"),
              cell: ({ row }: { row: any }) => (
                <Badge
                  variant={
                    row.original
                      .exemplaireDansEstampesMultiplesDescriptionStatus ===
                    "published"
                      ? "published"
                      : "draft"
                  }
                >
                  {row.original.exemplaireDansEstampesMultiplesDescriptionStatus ===
                  "published"
                    ? t("published")
                    : t("draft")}
                </Badge>
              ),
            },
          ]
        : [
            {
              accessorKey: "exemplaireDansEstampesMultiplesDescriptionStatus",
              header: t("exemplaireDansEstampesMultiplesDescriptionStatus"),
              cell: ({ row }: { row: any }) => (
                <Badge
                  variant={
                    row.original
                      .exemplaireDansEstampesMultiplesDescriptionStatus ===
                    "published"
                      ? "published"
                      : "draft"
                  }
                >
                  {row.original.exemplaireDansEstampesMultiplesDescriptionStatus ===
                  "published"
                    ? t("published")
                    : t("draft")}
                </Badge>
              ),
            },
          ]),
      {
        accessorKey: "exemplaireDansEstampesMultiplesDescriptionLocalisation",
        header: t("exemplaireDansEstampesMultiplesDescriptionLocalisation"),
        size: 200,
        cell: ({ row }) => (
          <TextCell
            value={
              row.original
                .exemplaireDansEstampesMultiplesDescriptionLocalisation
            }
          />
        ),
      },
      {
        accessorKey: "exemplaireDansEstampesMultiplesDescriptionNotes",
        header: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
        size: 220,
        cell: ({ row }) => (
          <HtmlCell
            value={
              row.original.exemplaireDansEstampesMultiplesDescriptionNotes ?? ""
            }
          />
        ),
      },
            createActionsColumnDef<Multiplesample>(({ row }) =>
        renderMultiplesampleRowActions(row.original),
      ),
    ];
  }, [renderMultiplesampleRowActions, t, type]);

  const STORAGE_KEY = "multiplesample-links-table";
  const defaultVisibility = { ...exemplairesColumnsVisibility, actions: true };
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    },
  );
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const relatedPaginationProps = {
    pagination: getPaginationRelated,
    setPage: setPaginationRelated,
    totalSize: countRelated?.[relationTable] ?? 0,
  };
  const addModeTable = (
    <DataTable
      columns={columns}
      data={notRelatedMultiplesamples?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notRelatedMultiplesamples?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnsOrder,
        state: { columnVisibility, columnOrder: columnsOrder },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columns}
      data={relatedFields ?? []}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnsOrder,
        state: { columnVisibility, columnOrder: columnsOrder },
      }}
    />
  );

  return (
    <div className="w-full">
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY}
        title={t("exemplaireTabExemplairesLies")}
        backLabel={t("linkedEntries")}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        }
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {(relatedFields ?? []).map((item: Multiplesample) => (
              <div key={item.id}>
                <GridCardWithActions actions={renderMultiplesampleRowActions(item)}>
                <MultiplesampleLinkGridCard
                  item={item}
                  t={t}
                  onTextClick={() => setSelectedMultiplesampleId(item.id)}
                />
              </GridCardWithActions>
              <ShowElements
                  type="multiplesample"
                  id={item.id}
                  open={selectedMultiplesampleId === item.id}
                  onOpenChange={(open) => {
                    if (!open) {
                      setSelectedMultiplesampleId(null);
                    }
                  }}
                  trigger={<span />}
                  elements={[
                    {
                      label: t(
                        "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
                      ),
                      value:
                        item.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t(
                        "exemplaireDansEstampesMultiplesDescriptionLegendeRedigee",
                      ),
                      value:
                        item.exemplaireDansEstampesMultiplesDescriptionLocalisation ??
                        "",
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: item.attachments ?? [],
                      type: "image",
                    },
                  ]}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    </div>
  );
};

export default MultiplesampleLinks;
